/**
 * Generates a cryptographically random code verifier for PKCE.
 * The verifier is a base64url-encoded string of 32 random bytes (43 characters).
 *
 * @returns A random code verifier string.
 */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Generates a code challenge from a code verifier using SHA-256.
 * Implements the S256 method from RFC 7636.
 *
 * @param codeVerifier The code verifier to hash.
 * @returns A promise that resolves to the base64url-encoded challenge.
 */
export async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(hash));
}

/**
 * Encodes a buffer to base64url format (URL-safe base64 without padding).
 *
 * @param buffer The buffer to encode.
 * @returns The base64url-encoded string.
 */
function base64UrlEncode(buffer: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...buffer));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}
